import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// Firestore imports removed
import { ApiService } from "../../services/api_service";
import "./EventsFiltersPage.css";

const EventsFiltersPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [priceFilter, setPriceFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dynamicCategories, setDynamicCategories] = useState([]);

  // Collapsible sections state
  const [sectionsOpen, setSectionsOpen] = useState({
    price: true,
    date: true,
    category: true
  });

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const priceOptions = ["Free", "0 - 500", "501 - 2000", "Above 2000"];
  const dateOptions = ["Today", "Tomorrow", "This Weekend"];

  // Extract unique categories from events
  useEffect(() => {
    if (events.length > 0) {
      const categories = new Set();
      events.forEach(event => {
        // Check tags array
        if (event.tags && Array.isArray(event.tags)) {
          event.tags.forEach(tag => categories.add(tag));
        }
        // Fallback to category and eventType
        if (event.category) categories.add(event.category);
        if (event.eventType) categories.add(event.eventType);
      });
      // Convert to array and sort
      setDynamicCategories(Array.from(categories).sort());
    }
  }, [events]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await ApiService.getAllEvents();
        let fetchedEvents = response.data || response;

        if (fetchedEvents && !Array.isArray(fetchedEvents) && typeof fetchedEvents === 'object') {
          fetchedEvents = Object.values(fetchedEvents);
        }
        if (!Array.isArray(fetchedEvents)) fetchedEvents = [];

        const eventsData = fetchedEvents.filter(event =>
          event.showInApp === true || event.showInApp === null || event.showInApp === undefined
        );

        // Sort events by startTime
        const sortedEvents = eventsData.sort((b, a) => {
          const dateA = a.startTime ? new Date(a.startTime) : new Date(0);
          const dateB = b.startTime ? new Date(b.startTime) : new Date(0);
          return dateA - dateB;
        });

        setEvents(sortedEvents);
        setFilteredEvents(sortedEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...events];

    // Price Filter
    if (priceFilter) {
      filtered = filtered.filter(event => {
        const price = getMinimumTicketPrice(event);
        if (priceFilter === "Free") return price === 0;
        if (priceFilter === "0 - 500") return price >= 0 && price <= 500;
        if (priceFilter === "501 - 2000") return price >= 501 && price <= 2000;
        if (priceFilter === "Above 2000") return price > 2000;
        return true;
      });
    }

    // Date Filter
    if (dateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter(event => {
        const eventDate = new Date(event.startTime);
        const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

        if (dateFilter === "Today") {
          return eventDateOnly.getTime() === today.getTime();
        }
        if (dateFilter === "Tomorrow") {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          return eventDateOnly.getTime() === tomorrow.getTime();
        }
        if (dateFilter === "This Weekend") {
          const thisWeekend = new Date(today);
          thisWeekend.setDate(thisWeekend.getDate() + (6 - thisWeekend.getDay()));
          return eventDateOnly.getTime() >= today.getTime() && eventDateOnly.getTime() <= thisWeekend.getTime();
        }
        return true;
      });
    }

    // Category Filter
    if (categoryFilter) {
      filtered = filtered.filter(event => {
        // Check tags array
        if (event.tags && Array.isArray(event.tags)) {
          const hasTag = event.tags.some(tag =>
            tag.toLowerCase().includes(categoryFilter.toLowerCase())
          );
          if (hasTag) return true;
        }
        // Fallback to category and eventType
        const eventCategory = event.category || event.eventType || '';
        return eventCategory.toLowerCase().includes(categoryFilter.toLowerCase());
      });
    }

    setFilteredEvents(filtered);
  }, [events, priceFilter, dateFilter, categoryFilter]);

  const getMinimumTicketPrice = (event) => {
    if (event.ticketPrice) return parseFloat(event.ticketPrice);
    if (event.tickets && event.tickets.length > 0) {
      return Math.min(...event.tickets.map(ticket => parseFloat(ticket.price || 0)));
    }
    return 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = dateStr.toDate ? dateStr.toDate() : new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    const dateOptions = { weekday: 'short', day: 'numeric', month: 'long' };
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };

    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

    return `${formattedDate} | ${formattedTime} Onwards`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#17BD3B]"></div>
      </div>
    );
  }

  return (
    <div className="filters-page-container">
      {/* Sidebar */}
      <div className="filters-sidebar custom-scrollbar">

        {/* Price Section */}
        <div className="filter-section">
          <div className="filter-header" onClick={() => toggleSection('price')}>
            <div className="filter-title">
              <span className={`transform transition-transform ${sectionsOpen.price ? 'rotate-180' : ''}`}>&#9662;</span> Price
            </div>
            {priceFilter && <button className="clear-btn" onClick={(e) => { e.stopPropagation(); setPriceFilter(""); }}>Clear</button>}
          </div>
          {sectionsOpen.price && (
            <div className="filter-content">
              {priceOptions.map(option => (
                <button
                  key={option}
                  className={`filter-pill ${priceFilter === option ? 'active' : ''}`}
                  onClick={() => setPriceFilter(priceFilter === option ? "" : option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Date Section */}
        <div className="filter-section">
          <div className="filter-header" onClick={() => toggleSection('date')}>
            <div className="filter-title">
              <span className={`transform transition-transform ${sectionsOpen.date ? 'rotate-180' : ''}`}>&#9662;</span> Date
            </div>
            {dateFilter && <button className="clear-btn" onClick={(e) => { e.stopPropagation(); setDateFilter(""); }}>Clear</button>}
          </div>
          {sectionsOpen.date && (
            <div className="filter-content">
              {dateOptions.map(option => (
                <button
                  key={option}
                  className={`filter-pill ${dateFilter === option ? 'active' : ''}`}
                  onClick={() => setDateFilter(dateFilter === option ? "" : option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Section */}
        <div className="filter-section">
          <div className="filter-header" onClick={() => toggleSection('category')}>
            <div className="filter-title">
              <span className={`transform transition-transform ${sectionsOpen.category ? 'rotate-180' : ''}`}>&#9662;</span> Category
            </div>
            {categoryFilter && <button className="clear-btn" onClick={(e) => { e.stopPropagation(); setCategoryFilter(""); }}>Clear</button>}
          </div>
          {sectionsOpen.category && (
            <div className="filter-content">
              {dynamicCategories.length > 0 ? (
                dynamicCategories.map(option => (
                  <button
                    key={option}
                    className={`filter-pill ${categoryFilter === option ? 'active' : ''}`}
                    onClick={() => setCategoryFilter(categoryFilter === option ? "" : option)}
                  >
                    {option}
                  </button>
                ))
              ) : (
                <div className="text-gray-500 text-xs italic px-2">No categories found</div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Main Content */}
      <div className="events-grid-container">
        <div className="events-grid">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className="filter-event-card cursor-pointer"
              onClick={() => navigate(`/events/${event.id}`)}
            >
              <div className="card-image-container">
                <img src={event.poster?.verticalUrl || event.poster?.horizontalUrl || event.posterUrl} alt={event.name} className="card-image" />
                <div className="card-overlay-tags">
                  {event.isTrending && <span className="tag trending">⚡ Trending</span>}
                  <span className="tag category">{event.category || 'Event'}</span>
                </div>
              </div>
              <div className="card-details">
                <h3 className="card-title text-white">{event.name}</h3>
                <p className="card-info">{formatDate(event.startTime)}</p>
                <p className="card-info">{event.eventLocation?.locationText || event.eventLocationText || event.location || 'Location TBD'}</p>
              </div>
            </div>
          ))}
        </div>

        {filteredEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center h-[50vh] text-white/50">
            <p className="text-xl mb-4">No events found matching your filters</p>
            <button
              onClick={() => { setPriceFilter(""); setDateFilter(""); setCategoryFilter(""); }}
              className="text-[#17BD3B] hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsFiltersPage;

